import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Alert,
  TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid } from '@mui/x-data-grid';
import axiosInstance from '../../../utils/axios';
import { fetchInventoryData } from '../../../atoms/logistics/LogisticsManagementAtom';
import { useAtom } from 'jotai';

const columnConfig = [
  { key: 1, value: 'delivery_name', hidden: false },
  { key: 2, value: 'delivered_qty', hidden: false },
  { key: 3, value: 'damaged_qty', hidden: false },
  { key: 4, value: 'remediated_qty', hidden: false }
];

const getColumns = (list, setList, originalData, setHasChanges, editType) => {
    return columnConfig
      .filter(col => !col.hidden)
      .map(col => {
        switch (col.value) {
          case 'delivery_name':
            return {
              field: 'delivery_name',
              headerName: 'Delivery Name',
              flex: 1,
              editable: false,
              headerAlign: 'center',
              sortable: false,
              align: 'center'
            };
  
          case 'delivered_qty':
            return {
              field: 'current_quantity',
              headerName: 'Delivered Qty',
              flex: 1,
              editable: false,
              type: 'number',
              headerAlign: 'center',
              sortable: false,
              align: 'center'
            };
  
          case 'damaged_qty':
            return {
              field: 'damaged',
              headerName: 'Damaged Qty',
              flex: 1,
              editable: false,
              type: 'number',
              headerAlign: 'center',
              sortable: false,
              align: 'center',
              renderCell: (params) => {
                if (editType === 'damaged') {
                  const handleChange = (event) => {
                    const newValue = Number(event.target.value) || 0;
                    const maxValue = params.row.current_quantity || 0;
  
                    if (newValue >= 0 && newValue <= maxValue) {
                      const updatedList = list.map(row =>
                        row.id === params.row.id
                          ? { ...row, damaged: newValue }
                          : row
                      );
                      setList(updatedList);
  
                      const changes = updatedList.some(item => {
                        const originalItem = originalData.find(orig => orig.id === item.id);
                        return originalItem && item.damaged !== originalItem.damaged;
                      });
                      setHasChanges(changes);
                    }
                  };
  
                  return (
                    <TextField
                      type="number"
                      size="small"
                      value={params.value || 0}
                      onChange={handleChange}
                      inputProps={{
                        min: 0,
                        max: params.row.current_quantity || 0,
                        step: 1,
                        style: { textAlign: 'center' }
                      }}
                      sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': { height: '32px' }
                      }}
                      helperText={`Max: ${params.row.current_quantity || 0}`}
                      FormHelperTextProps={{
                        sx: {
                          fontSize: '10px',
                          textAlign: 'center',
                          margin: 0,
                          paddingTop: '2px'
                        }
                      }}
                    />
                  );
                } else {
                  return <span>{params.value || 0}</span>;
                }
              }
            };
  
          case 'remediated_qty':
            return {
              field: 'remediated_quantity',
              headerName: 'Remediated Qty',
              flex: 1,
              editable: false,
              type: 'number',
              headerAlign: 'center',
              sortable: false,
              align: 'center',
              renderCell: (params) => {
                if (editType === 'remediate') {
                  const handleChange = (event) => {
                    const newValue = Number(event.target.value) || 0;
                    const maxValue = params.row.damaged || 0;
  
                    if (newValue >= 0 && newValue <= maxValue) {
                      const updatedList = list.map(row =>
                        row.id === params.row.id
                          ? { ...row, remediated_quantity: newValue }
                          : row
                      );
                      setList(updatedList);
  
                      const changes = updatedList.some(item => {
                        const originalItem = originalData.find(orig => orig.id === item.id);
                        return originalItem && item.remediated_quantity !== originalItem.remediated_quantity;
                      });
                      setHasChanges(changes);
                    }
                  };
  
                  return (
                    <TextField
                      type="number"
                      size="small"
                      value={params.value || 0}
                      onChange={handleChange}
                      inputProps={{
                        min: 0,
                        max: params.row.damaged || 0,
                        step: 1,
                        style: { textAlign: 'center' }
                      }}
                      sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': { height: '32px' }
                      }}
                      helperText={`Max: ${params.row.damaged || 0}`}
                      FormHelperTextProps={{
                        sx: {
                          fontSize: '10px',
                          textAlign: 'center',
                          margin: 0,
                          paddingTop: '2px'
                        }
                      }}
                    />
                  );
                } else {
                  return <span>{params.value || 0}</span>;
                }
              }
            };
  
          default:
            return null;
        }
      })
      .filter(Boolean);
  };
  

const RemediateDialog = ({ open, onClose, rowData, current, editType }) => {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [btnLoader, setBtnLoader] = useState(false);
  const [, setInventory] = useAtom(fetchInventoryData);

  useEffect(() => {
    if (open && rowData) {
      fetchTableData(rowData);
    }
  }, [open, rowData]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setList([]);
      setOriginalData([]);
      setHasChanges(false);
    }
  }, [open]);

  const fetchTableData = async (value) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `/projects/inventory/laydown-count?bom_id=${value?.bom_id}`
      );
      if (res.data) {
        const enriched = res.data.map((item) => ({
          ...item,
          id: item.delivery_laydown_id || item.laydown_id || item.delivery_id,
          remediated_quantity: item.remediated_quantity || 0
        }));
        setList(enriched);
        setOriginalData(enriched.map(item => ({ ...item })));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setBtnLoader(true);
    try {      
        let result = []
        if(editType === "remediate") {
            result = list.map((item) => {
                return {
                    bom_delivery_laydown_id : item?.bom_delivery_laydown_id,
                    remediated_quantity : item?.remediated_quantity
                }
            })
        } else {
            result = list.map((item) => {
                return {
                    bom_delivery_laydown_id : item?.bom_delivery_laydown_id,
                    damaged_quantity : item?.damaged
                }
            })
        }
        
        const res = await axiosInstance.post(`/projects/inventory/remediated-count/bom-delivery-laydown`, result);

        if(res) {
            await setInventory(current.id);
            onClose();
        }
    } catch (error) {
      console.error('Error saving remediated quantities:', error);
    } finally {
        setBtnLoader(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const columns = getColumns(list, setList, originalData, setHasChanges, editType);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={600}>
            Remediate Quantity
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ pt: 2 }}>
            <Typography fontSize={18} gutterBottom>
              {rowData?.description || rowData?.item_code}
            </Typography>
            
            {hasChanges && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                You have unsaved changes. Click Save to apply changes.
              </Alert>
            )}
            
            <Box sx={{ width: '100%', mt: 2 }}>
              <DataGrid
                rows={list}
                columns={columns}
                loading={loading}
                disableRowSelectionOnClick
                disableColumnMenu
                hideFooter={list.length <= 5}
                pageSizeOptions={[5, 10, 25]}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 5 },
                  },
                }}
                sx={{
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '& .MuiDataGrid-columnHeader': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      color: 'white',
                      fontWeight: 600,
                    },
                    '& .MuiDataGrid-iconSeparator': {
                      color: 'white',
                    },
                    '& .MuiDataGrid-sortIcon': {
                      color: 'white',
                    },
                    '& .MuiDataGrid-menuIcon': {
                      color: 'white',
                    }
                  },
                  '& .MuiDataGrid-cell': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  },
                  '& .MuiDataGrid-row': {
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }
                }}
              />
            </Box>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Enter values in the Remediated Qty input fields. Maximum value is limited by Damaged Qty.
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={loading || !hasChanges}
          loading={btnLoader}
        >
          {loading ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RemediateDialog;